import React, { useState, useEffect, useCallback } from "react";
import { siteSettingsAPI } from "../services/api";
import toast from "react-hot-toast";

interface LocalHeroBanner {
  image: string;
  title: string;
  titleEn: string;
  subtitle: string;
  subtitleEn: string;
  order: number;
}

interface MarkupState {
  enabled: boolean;
  percent: number | string;
  label: string;
}

const compressImage = (file: File, maxWidth = 1920, quality = 0.8): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Lỗi khi đọc file ảnh"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Lỗi khi tải ảnh"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Lỗi canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Lỗi khi nén ảnh"));
              return;
            }
            const compressedReader = new FileReader();
            compressedReader.onerror = () => reject(new Error("Lỗi khi đọc ảnh đã nén"));
            compressedReader.onloadend = () => resolve(compressedReader.result as string);
            compressedReader.readAsDataURL(blob);
          },
          "image/jpeg",
          quality
        );
      };
      img.src = (e.target as FileReader).result as string;
    };
    reader.readAsDataURL(file);
  });
};

// Danh sách ngân hàng VN phổ biến
function SiteSettingsPage(): React.ReactElement {
  const [banners, setBanners] = useState<LocalHeroBanner[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [showPreview, setShowPreview] = useState<boolean>(false);


  // Tagline state
  const [tagline, setTagline] = useState({ tagline: "", taglineEn: "" });
  const [savingTagline, setSavingTagline] = useState(false);

  // SEO state
  const [seo, setSeo] = useState({ seoTitle: "", seoDescription: "" });
  const [savingSEO, setSavingSEO] = useState(false);

  // Hiển thị mục trên trang chủ
  const [showReviews, setShowReviews] = useState<boolean>(false);
  const [savingSections, setSavingSections] = useState<boolean>(false);

  // Holiday markup state
  const [markup, setMarkup] = useState<MarkupState>({ enabled: false, percent: 0, label: "Phụ thu dịp lễ" });
  const [savingMarkup, setSavingMarkup] = useState<boolean>(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await siteSettingsAPI.getSettings();
      const data = response.data.data;
      setBanners((data.heroBanners || []).map((b: any) => ({
        ...b,
        titleEn: b.titleEn || "",
        subtitleEn: b.subtitleEn || "",
      })));
      if (data.holidayMarkup) setMarkup(data.holidayMarkup);
      setShowReviews(Boolean(data.sections?.showReviews));
      setSeo({
        seoTitle: data.seoTitle || "23Homestay - Homestay chất lượng tại TP.HCM",
        seoDescription: data.seoDescription || "Hệ thống homestay chất lượng cao tại TP. Hồ Chí Minh. Không gian ấm cúng, tiện nghi hiện đại, giá cả hợp lý.",
      });
      setTagline({
        tagline: data.tagline || "Ngôi nhà ấm cúng của bạn tại Sài Gòn",
        taglineEn: data.taglineEn || "Your cozy home in Saigon",
      });
    } catch (error) {
      toast.error("Lỗi khi tải cài đặt");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSaveSEO = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSavingSEO(true);
    try {
      await siteSettingsAPI.updateSEO(seo);
      toast.success("Đã lưu cài đặt SEO");
    } catch {
      toast.error("Lưu thất bại");
    } finally {
      setSavingSEO(false);
    }
  };

  const handleSaveTagline = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSavingTagline(true);
    try {
      await siteSettingsAPI.updateTagline(tagline);
      toast.success("Đã lưu tagline");
    } catch {
      toast.error("Lưu thất bại");
    } finally {
      setSavingTagline(false);
    }
  };

  // Bật/tắt mục Đánh giá trên trang chủ — lưu ngay khi bấm, lỗi thì trả về trạng thái cũ
  const toggleShowReviews = async (): Promise<void> => {
    const next = !showReviews;
    setShowReviews(next);
    setSavingSections(true);
    try {
      await siteSettingsAPI.updateSections({ showReviews: next });
      toast.success(next ? "Đã hiện mục Đánh giá trên trang chủ" : "Đã ẩn mục Đánh giá khỏi trang chủ");
    } catch {
      setShowReviews(!next);
      toast.error("Lưu thất bại");
    } finally {
      setSavingSections(false);
    }
  };

  const handleSaveMarkup = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const pct = Number(markup.percent);
    if (isNaN(pct) || pct < 0 || pct > 500) {
      toast.error("Phần trăm phụ thu phải từ 0 đến 500");
      return;
    }
    setSavingMarkup(true);
    try {
      await siteSettingsAPI.updateHolidayMarkup({ ...markup, percent: pct });
      toast.success("Đã lưu cài đặt phụ thu dịp lễ");
    } catch {
      toast.error("Lưu thất bại");
    } finally {
      setSavingMarkup(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const newBanners: LocalHeroBanner[] = [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} không phải là file ảnh`);
          continue;
        }
        const compressed = await compressImage(file, 1920, 0.8);
        newBanners.push({
          image: compressed,
          title: "",
          titleEn: "",
          subtitle: "",
          subtitleEn: "",
          order: banners.length + newBanners.length,
        });
      }

      const updated = [...banners, ...newBanners];

      // Check total size
      const totalSize = JSON.stringify(updated).length;
      if (totalSize > 14 * 1024 * 1024) {
        toast.error("Tổng kích thước ảnh quá lớn (giới hạn ~14MB). Hãy giảm số lượng hoặc chất lượng ảnh.");
        return;
      }
      if (totalSize > 10 * 1024 * 1024) {
        toast("Cảnh báo: Tổng kích thước gần giới hạn cho phép", { icon: "⚠️" });
      }

      setBanners(updated);
      toast.success(`Đã thêm ${newBanners.length} banner`);
    } catch (error) {
      toast.error("Lỗi khi upload ảnh: " + (error as Error).message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeBanner = (index: number): void => {
    setBanners((prev) => prev.filter((_, i) => i !== index));
  };

  const moveBanner = (index: number, direction: number): void => {
    const newBanners = [...banners];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newBanners.length) return;
    [newBanners[index], newBanners[targetIndex]] = [newBanners[targetIndex], newBanners[index]];
    newBanners.forEach((b, i) => (b.order = i));
    setBanners(newBanners);
  };

  const updateBannerField = (index: number, field: keyof LocalHeroBanner, value: string): void => {
    setBanners((prev) => prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)));
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const sorted = banners.map((b, i) => ({ ...b, order: i }));
      await siteSettingsAPI.updateHeroBanners(sorted);
      toast.success("Đã lưu hero banners thành công!");
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Lỗi khi lưu");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-500">Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Quản lý trang chủ</h1>
          <p className="text-gray-500 mt-1">Quản lý hero banner slideshow trên trang chủ public</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-coffee-600 hover:bg-coffee-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {saving ? "Đang lưu..." : "💾 Lưu thay đổi"}
        </button>
      </div>

      {/* Upload area */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Hero Banners ({banners.length})</h2>

        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-coffee-400 hover:bg-coffee-50 transition-colors">
          <div className="text-center">
            {uploading ? (
              <p className="text-coffee-600 font-medium">Đang xử lý ảnh...</p>
            ) : (
              <>
                <p className="text-3xl mb-1">📷</p>
                <p className="text-gray-600 font-medium">Click để upload banner</p>
                <p className="text-gray-400 text-sm">Hỗ trợ JPG, PNG. Nên dùng ảnh ngang 1920x600+</p>
              </>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
            disabled={uploading}
          />
        </label>

        {banners.length > 0 && (
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => setShowPreview(true)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              👁️ Xem preview slideshow
            </button>
          </div>
        )}
      </div>

      {/* Banner list */}
      {banners.length > 0 && (
        <div className="space-y-4">
          {banners.map((banner, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {/* Image preview */}
                <div className="md:w-80 h-48 md:h-auto flex-shrink-0 bg-gray-100 relative">
                  <img
                    src={banner.image}
                    alt={`Banner ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    #{index + 1}
                  </span>
                </div>

                {/* Controls */}
                <div className="flex-1 p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề (VI)</label>
                      <input
                        type="text"
                        value={banner.title}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBannerField(index, "title", e.target.value)}
                        placeholder="Ví dụ: Không gian nghỉ dưỡng ấm cúng"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title (EN)</label>
                      <input
                        type="text"
                        value={banner.titleEn ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBannerField(index, "titleEn", e.target.value)}
                        placeholder="E.g: Cozy retreat space"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phụ đề (VI)</label>
                      <input
                        type="text"
                        value={banner.subtitle}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBannerField(index, "subtitle", e.target.value)}
                        placeholder="Ví dụ: Trải nghiệm homestay tại Sài Gòn"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle (EN)</label>
                      <input
                        type="text"
                        value={banner.subtitleEn ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBannerField(index, "subtitleEn", e.target.value)}
                        placeholder="E.g: Your cozy home in Saigon"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      onClick={() => moveBanner(index, -1)}
                      disabled={index === 0}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm disabled:opacity-30 transition-colors"
                    >
                      ⬆️ Lên
                    </button>
                    <button
                      onClick={() => moveBanner(index, 1)}
                      disabled={index === banners.length - 1}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm disabled:opacity-30 transition-colors"
                    >
                      ⬇️ Xuống
                    </button>
                    <button
                      onClick={() => removeBanner(index)}
                      className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded text-sm transition-colors ml-auto"
                    >
                      🗑️ Xóa
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {banners.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <p className="text-4xl mb-3">🖼️</p>
          <p className="text-gray-500 text-lg">Chưa có banner nào</p>
          <p className="text-gray-400 mt-1">Upload ảnh để tạo slideshow trên trang chủ</p>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && banners.length > 0 && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-lg font-medium"
            >
              ✕ Đóng
            </button>

            <div className="relative rounded-xl overflow-hidden">
              <img
                src={banners[previewIndex].image}
                alt={`Preview ${previewIndex + 1}`}
                className="w-full h-[400px] md:h-[500px] object-cover"
              />

              {/* Overlay text */}
              {(banners[previewIndex].title || banners[previewIndex].subtitle) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end">
                  <div className="p-8 text-white">
                    {banners[previewIndex].title && (
                      <h2 className="text-3xl font-bold">{banners[previewIndex].title}</h2>
                    )}
                    {banners[previewIndex].subtitle && (
                      <p className="text-lg mt-2 text-gray-200">{banners[previewIndex].subtitle}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation arrows */}
              {banners.length > 1 && (
                <>
                  <button
                    onClick={() => setPreviewIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1))}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/30 hover:bg-white/50 rounded-full flex items-center justify-center text-white text-xl transition-colors"
                  >
                    ‹
                  </button>
                  <button
                    onClick={() => setPreviewIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1))}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/30 hover:bg-white/50 rounded-full flex items-center justify-center text-white text-xl transition-colors"
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            {/* Dots */}
            {banners.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPreviewIndex(i)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      i === previewIndex ? "bg-white" : "bg-white/40"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tagline ───────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Tagline (Footer)</h2>
        <p className="text-sm text-gray-500 mb-6">
          Dòng mô tả hiển thị ở footer trang public — hỗ trợ 2 ngôn ngữ.
        </p>
        <form onSubmit={handleSaveTagline} className="space-y-4 max-w-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tiếng Việt</label>
            <input
              type="text"
              value={tagline.tagline}
              onChange={(e) => setTagline((t) => ({ ...t, tagline: e.target.value }))}
              maxLength={200}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">English</label>
            <input
              type="text"
              value={tagline.taglineEn}
              onChange={(e) => setTagline((t) => ({ ...t, taglineEn: e.target.value }))}
              maxLength={200}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={savingTagline}
            className="px-6 py-2.5 bg-coffee-600 text-white rounded-lg font-medium hover:bg-coffee-700 disabled:opacity-50 flex items-center gap-2"
          >
            {savingTagline && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            Lưu tagline
          </button>
        </form>
      </div>

      {/* ── SEO Settings ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1">SEO — Tiêu đề & Mô tả Google</h2>
        <p className="text-sm text-gray-500 mb-6">
          Nội dung hiển thị khi trang web xuất hiện trên kết quả tìm kiếm Google.
        </p>
        <form onSubmit={handleSaveSEO} className="space-y-4 max-w-2xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tiêu đề trang (SEO Title)
              <span className="ml-2 text-gray-400 font-normal">({seo.seoTitle.length}/120 ký tự)</span>
            </label>
            <input
              type="text"
              value={seo.seoTitle}
              onChange={(e) => setSeo((s) => ({ ...s, seoTitle: e.target.value }))}
              maxLength={120}
              placeholder="23Homestay - Homestay chất lượng tại TP.HCM"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Nên từ 50–70 ký tự để hiển thị tốt trên Google.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mô tả trang (SEO Description)
              <span className="ml-2 text-gray-400 font-normal">({seo.seoDescription.length}/320 ký tự)</span>
            </label>
            <textarea
              value={seo.seoDescription}
              onChange={(e) => setSeo((s) => ({ ...s, seoDescription: e.target.value }))}
              maxLength={320}
              rows={3}
              placeholder="Hệ thống homestay chất lượng cao tại TP. Hồ Chí Minh..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">Nên từ 120–160 ký tự để hiển thị tốt trên Google.</p>
          </div>
          {/* Preview */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Preview Google</p>
            <p className="text-blue-600 text-base font-medium truncate">
              {seo.seoTitle || "23Homestay - Homestay chất lượng tại TP.HCM"}
            </p>
            <p className="text-green-700 text-xs">https://www.23homestay.vn</p>
            <p className="text-gray-600 text-sm mt-1 line-clamp-2">
              {seo.seoDescription || "Hệ thống homestay chất lượng cao tại TP. Hồ Chí Minh..."}
            </p>
          </div>
          <button
            type="submit"
            disabled={savingSEO}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {savingSEO && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            Lưu SEO
          </button>
        </form>
      </div>

      {/* ── Hiển thị mục trên trang chủ ─────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Hiển thị trên trang chủ</h2>
        <p className="text-sm text-gray-500 mb-5">Bật hoặc tắt từng mục. Có hiệu lực ngay, khách tải lại trang là thấy.</p>
        <div className="flex items-center justify-between gap-4 max-w-lg">
          <div>
            <p className="font-medium text-gray-900">Mục “Đánh giá từ khách hàng”</p>
            <p className="text-sm text-gray-500">
              {showReviews ? "Đang hiện — kèm nút “Đánh giá” trên menu" : "Đang ẩn — chưa có đánh giá thật thì nên để ẩn"}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showReviews}
            aria-label="Hiện mục Đánh giá trên trang chủ"
            disabled={savingSections}
            onClick={toggleShowReviews}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${
              showReviews ? "bg-green-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                showReviews ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* ── Holiday Markup ───────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Phụ thu dịp lễ</h2>
        <p className="text-sm text-gray-500 mb-6">
          Khi bật, giá tất cả phòng sẽ được nhân thêm theo phần trăm đã cài. Tắt để trở về giá gốc.
        </p>

        <form onSubmit={handleSaveMarkup} className="space-y-4 max-w-lg">
          {/* Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMarkup((m) => ({ ...m, enabled: !m.enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                markup.enabled ? "bg-orange-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  markup.enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${markup.enabled ? "text-orange-600" : "text-gray-500"}`}>
              {markup.enabled ? "Đang bật — giá đã tăng thêm" : "Đang tắt — giá bình thường"}
            </span>
          </div>

          {/* Percent */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phần trăm phụ thu (%)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="500"
                step="1"
                value={markup.percent}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMarkup((m) => ({ ...m, percent: e.target.value }))}
                className="w-28 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 text-sm"
              />
              <span className="text-gray-500 text-sm">%</span>
              {Number(markup.percent) > 0 && (
                <span className="text-xs text-orange-600 font-medium ml-2">
                  Ví dụ: phòng 200.000đ → {Math.round(200000 * (1 + Number(markup.percent) / 100)).toLocaleString("vi-VN")}đ
                </span>
              )}
            </div>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tên hiển thị (trên hóa đơn)</label>
            <input
              type="text"
              value={markup.label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMarkup((m) => ({ ...m, label: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 text-sm"
              placeholder="Phụ thu dịp lễ"
              maxLength={100}
            />
          </div>

          <button
            type="submit"
            disabled={savingMarkup}
            className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
          >
            {savingMarkup && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            Lưu cài đặt phụ thu
          </button>
        </form>
      </div>

    </div>
  );
}

export default SiteSettingsPage;
